// LA HOJA DEL DOCUMENTO: LO QUE EL CLIENTE VA A VER.
//
// Lissette, 2026-09-22: *"las vista de las firmas cuando se edite o se haga una
// nueva, de la version de vista como se vera cuando se envie, porque cuando la
// hacen y la envia sale diferente movida no centrada y quieren verla antes"* —
// y, con la captura delante: *"mira en oscuro ahora no se ve"*.
//
// ── LAS DOS QUEJAS SON EL MISMO FALLO ───────────────────────────────────────
//
// La página que abre el cliente pinta el documento sobre una HOJA BLANCA: fondo
// `#fff`, texto `#1a1a1a`, 3rem de margen, y —esto es lo importante— la FUENTE,
// EL TAMAÑO y LA ALINEACIÓN que la plantilla tiene guardados.
//
// Las dos vistas previas del CRM no hacían nada de eso: soltaban el HTML del
// documento encima de la tarjeta oscura de la aplicación. De ahí salen las dos
// cosas que se reportaron, que no son dos:
//
//   · "sale diferente, movida, no centrada" → la vista previa ignoraba el
//     `formatting` de la plantilla, que es justo lo que decide dónde cae cada
//     cosa cuando se envía. Se revisaba una cosa y se mandaba otra.
//   · "en oscuro no se ve" → un documento trae su propio color de texto (casi
//     siempre negro, sea escrito a mano o pegado de Word). Negro sobre el fondo
//     oscuro del CRM es un documento invisible. En claro se leía por pura
//     suerte: el fondo de la tarjeta era claro.
//
// ── POR QUÉ UN COMPONENTE Y NO UNOS ESTILOS COPIADOS ────────────────────────
//
// Porque "cómo se verá cuando se envíe" solo es verdad si es LA MISMA hoja. Dos
// copias de estos estilos vuelven a separarse en el primer retoque, y entonces
// la vista previa miente otra vez — que es exactamente lo que se está
// arreglando. La página pública y las dos vistas del CRM entran por aquí.
//
// El CUERPO no se pinta aquí: lo pasa quien llama, porque no es el mismo en los
// dos sitios. En la página pública el texto se parte por sus preguntas clavadas
// y entre trozo y trozo va un control que el cliente rellena; en el CRM es el
// documento a secas. Lo que sí es común —y es lo que se perdía— es la hoja y el
// estilo del cuerpo.

// El formato guardado vive en el `.js` para que se pueda probar: desde una
// prueba no se puede importar un `.jsx` (regla 48).
import { estiloDelCuerpo, formatoDelDocumento } from '../documentoConFormato.js';

/**
 * @param {object}  props
 * @param {string}  props.body       el cuerpo, para saber si lleva formato
 * @param {string}  props.title      el título del documento, o vacío
 * @param {string}  props.logoUrl    el logo de la agencia, o vacío
 * @param {object}  props.formatting el formato guardado de la plantilla
 * @param {boolean} props.compacta   true en las vistas previas del CRM, que
 *                                   viven en una tarjeta estrecha: se recorta
 *                                   el margen de la hoja y nada más. El fondo,
 *                                   el color, la fuente, el tamaño y la
 *                                   alineación NO cambian — son justamente lo
 *                                   que se viene a comprobar.
 * @param {any}     props.children   el cuerpo ya pintado
 */
export default function HojaDelDocumento({ body, title, logoUrl, formatting, compacta = false, children }) {
  const { font, fontSize, align } = formatoDelDocumento(formatting);
  return (
    <div style={{
      background: '#fff',
      color: '#1a1a1a',
      borderRadius: 12,
      padding: compacta ? '1.1rem 1.25rem 1.3rem' : '3rem 3rem 3.5rem',
      boxShadow: '0 6px 24px rgba(0,0,0,.4)',
      fontFamily: font,
      lineHeight: 1.7,
      fontSize: 15,
    }}>
      {/* El logo de la agencia DENTRO del documento (Lissette 2026-06-21 #14:
          "el logo también en el documento como tal, que el cliente lo vea"). */}
      {logoUrl && <img src={logoUrl} alt="" style={{ maxHeight: compacta ? 40 : 72, maxWidth: compacta ? 160 : 280, objectFit: 'contain', display: 'block', margin: compacta ? '0 0 .8rem' : '0 0 1.4rem' }} />}
      {title && (
        <h2 style={{
          margin: compacta ? '0 0 .7rem' : '0 0 1.2rem',
          fontSize: compacta ? '1rem' : '1.3rem',
          fontWeight: 700,
          borderBottom: '1px solid #ddd',
          paddingBottom: '.6rem',
          color: '#1a1a1a',
        }}>{title}</h2>
      )}
      <div style={{ ...estiloDelCuerpo(body), fontSize, textAlign: align }}>
        {children}
      </div>
    </div>
  );
}
